import React from 'react';
import { useTranslation } from 'react-i18next';
import useServiceRequestsData from '../../hooks/useServiceRequestsData';
import { getRequestIcon, getRequestLabel, formatTimeAgo } from './serviceRequestHelpers';
import './ServiceRequestsPanel.css';

const ServiceRequestsPanel = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { requests, loading, soundEnabled, setSoundEnabled, acknowledge, complete } =
    useServiceRequestsData(restaurantId);

  if (!restaurantId) return null;

  return (
    <div className="service-requests-panel">
      <div className="panel-header">
        <h2>{t('service.panel.title', { defaultValue: 'Service Requests' })}</h2>
        <div className="panel-actions">
          <span className="request-count">
            {requests.length} {t('service.panel.active', { defaultValue: 'active' })}
          </span>
          <button
            type="button"
            className={`sound-toggle ${soundEnabled ? 'enabled' : ''}`}
            onClick={() => setSoundEnabled(!soundEnabled)}
            aria-label={soundEnabled ? 'Mute notifications' : 'Enable sound'}
          >
            {soundEnabled ? '🔔' : '🔕'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="service-requests-loading">
          <div className="loading" />
          <span>{t('common.loading', { defaultValue: 'Loading...' })}</span>
        </div>
      ) : requests.length === 0 ? (
        <div className="empty-requests">
          <p>
            ✅
            {t('service.panel.noPending', { defaultValue: 'No pending service requests' })}
          </p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => (
            <div
              key={request.id}
              className={`request-card priority-${request.priority || 'medium'} status-${
                request.status
              }`}
            >
              <div className="request-header">
                <span className="request-icon" aria-hidden="true">
                  {getRequestIcon(request.request_type)}
                </span>
                <div className="request-info">
                  <h3 className="request-title">
                    {t('common.table', { defaultValue: 'Table' })} {request.table_number}
                  </h3>
                  <p className="request-type">{getRequestLabel(t, request.request_type)}</p>
                </div>
                <span className="request-time">{formatTimeAgo(t, request.created_at)}</span>
              </div>

              {request.message && (
                <div className="request-message">&quot;{request.message}&quot;</div>
              )}

              <div className="request-actions">
                {request.status === 'pending' && (
                  <button
                    type="button"
                    className="btn-acknowledge"
                    onClick={() => acknowledge(request.id)}
                  >
                    {t('service.panel.acknowledge', { defaultValue: 'Acknowledge' })}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-complete"
                  onClick={() => complete(request.id)}
                >
                  {request.status === 'acknowledged'
                    ? t('service.panel.complete', { defaultValue: 'Complete' })
                    : t('service.panel.done', { defaultValue: 'Done' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceRequestsPanel;
